import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStoreFeature,
  type,
  withComputed,
  withMethods,
} from '@ngrx/signals';
import { DataLayerTypeState } from './data-layer-type.state';
import { StateFilter } from '../crud.state';
import { DataLayerType } from '../../models/data-layer-type';
import { SafeHtml } from '@angular/platform-browser';
import { IconService } from '../../services/icon/icon.service';
import { catchError, map, Observable, of } from 'rxjs';
export interface DataLayerTypeIcon {
  key: string;
  svg: SafeHtml | undefined;
  color: string;
}
export const filterItems = (items: DataLayerType[], filter: StateFilter) => {
  return items
    .filter(item => {
      const titleMatch = item.name
        .toLowerCase()
        .includes(filter.query.toLowerCase());
      const tagsMatch =
        !filter.tags ||
        filter.tags.length === 0 ||
        (item.tags &&
          filter.tags.some(tagId => item.tags && item.tags.includes(tagId)));

      return titleMatch && tagsMatch;
    })
    .sort((a, b) => a.sorting - b.sorting);
};

export function withDataLayerTypeSelectors() {
  return signalStoreFeature(
    {
      state: type<DataLayerTypeState>(),
    },
    withComputed(({ items, filter }) => ({
      count: computed(() => Object.keys(items()).length),
      asArray: computed(() =>
        Object.values(items()).sort((a, b) => a.sorting - b.sorting)
      ),
      typeSorted: computed(() =>
        Object.values(items()).sort((a, b) =>
          a.type === b.type
            ? a.sorting - b.sorting
            : a.type === 'Polygon'
              ? -1
              : 1
        )
      ),
      polygonLayers: computed(() =>
        Object.values(items())
          .filter(type => type.type === 'Polygon')
          .sort((a, b) => a.sorting - b.sorting)
      ),
      pointLayers: computed(() =>
        Object.values(items())
          .filter(type => type.type === 'Point')
          .sort((a, b) => a.sorting - b.sorting)
      ),
      filteredItems: computed(() => {
        let types = Object.values(items());
        if (filter()) {
          types = filterItems(types, filter());
        }
        return types as DataLayerType[];
      }),
      groupedByType: computed(() => {
        const grouped = filterItems(Object.values(items()), filter()).reduce(
          (acc, dataLayerType) => {
            const type = dataLayerType.type;
            if (!acc[type]) acc[type] = [];
            acc[type].push(dataLayerType);
            return acc;
          },
          {} as Record<'Point' | 'Polygon', DataLayerType[]>
        );

        // Sort each group
        Object.values(grouped).forEach(group =>
          group.sort((a, b) => a.sorting - b.sorting)
        );

        return grouped;
      }),
      workspaceIds: computed(() => {
        const types: DataLayerType[] = Object.values(items());
        return [
          ...new Set(
            types.flatMap((item: DataLayerType) => item['workspaces'])
          ),
        ];
      }),
    })),
    withMethods(
      (
        { items, filter, groupedByType },
        iconService = inject(IconService)
      ) => ({
        // `[]` when the commune has no types of this kind - callers iterate
        // the result (type dropdowns, POI helper) and crashed on `undefined`
        getByType(type: 'Point' | 'Polygon'): DataLayerType[] {
          return groupedByType()[type] ?? [];
        },
        globalSearch(
          query: string,
          tags: string[],
          type?: 'Polygon' | 'Point'
        ): DataLayerType[] {
          let itemsArray = Object.values(items());
          if (type) {
            itemsArray = itemsArray.filter(item => item.type === type);
          }
          const tempFilter: StateFilter = { ...filter(), query, tags };
          return filterItems(itemsArray, tempFilter);
        },
        getPoiIcon(id: string): Observable<DataLayerTypeIcon | null> | null {
          const item = items()[id];
          if (item && item.icon) {
            const svgCodeObservable = iconService.getSvgCodeByKey(item.icon);
            if (svgCodeObservable) {
              return svgCodeObservable.pipe(
                map(svgCode => ({
                  key: item.icon || '',
                  svg: svgCode,
                  color: item.color || '#000000',
                })),
                catchError(() => of(null)) // Return null on error
              );
            }
            return of(null);
          }
          return of(null);
        },
        isPointType(id: string): boolean {
          return items()[id]?.type === 'Point' || false;
        },
        filterByWorkspace(workspaceId: string, type?: 'Point' | 'Polygon') {
          const groupKey = workspaceId.includes('#')
            ? workspaceId.split('#')[1]
            : undefined;
          const baseWorkspaceId = workspaceId.split('#')[0];

          const matchesWorkspace = (item: DataLayerType): boolean => {
            return (
              !!item.workspaces && item.workspaces.includes(baseWorkspaceId)
            );
          };

          const matchesTypeFilter = (item: DataLayerType): boolean => {
            return !type || item.type === type;
          };

          return Object.values(items())
            .filter(item => matchesWorkspace(item) && matchesTypeFilter(item))
            .map((item: DataLayerType) =>
              groupKey ? `${item._id}#${groupKey}` : item._id
            ) as string[];
        },
      })
    )
  );
}
